import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Table, Card, DatePicker, Button, Tag, message } from "antd";
import { ExportOutlined, ReloadOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import moment from "moment";
import { getAllAppointments } from "../redux/adminSlice";

const { RangePicker } = DatePicker;

export default function ManageAppointments() {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [dateRange, setDateRange] = useState(null);

  const fetchAppointments = useCallback(() => {
    setLoading(true);
    const request = {
      startDate: dateRange?.[0] ? dateRange[0].startOf("day").toISOString() : null,
      endDate: dateRange?.[1] ? dateRange[1].endOf("day").toISOString() : null,
      page,
      pageSize,
      sortDescending: true,
    };

    dispatch(getAllAppointments(request))
      .then((response) => {
        if (getAllAppointments.fulfilled.match(response)) {
          const payload = response.payload;
          const rows = payload?.data ?? payload?.Data ?? [];
          setAppointments(rows);
          setTotalRecords(payload?.totalRecords ?? payload?.TotalRecords ?? rows.length);
        } else {
          message.error(
            response?.payload?.message || response?.payload?.Message || "Failed to load appointments.",
          );
          setAppointments([]);
          setTotalRecords(0);
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, page, pageSize, dateRange]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const columns = useMemo(
    () => [
      {
        title: "Patient",
        dataIndex: "patientName",
        key: "patientName",
        render: (value, record) => value ?? record.PatientName ?? "—",
      },
      {
        title: "Doctor",
        dataIndex: "doctorName",
        key: "doctorName",
        render: (value, record) => value ?? record.DoctorName ?? "—",
      },
      {
        title: "Date & Time",
        dataIndex: "appointmentDate",
        key: "appointmentDate",
        render: (value, record) => {
          const date = value ?? record.AppointmentDate;
          return date ? moment(date).format("MMM D, YYYY h:mmA") : "—";
        },
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        render: (value, record) => value ?? record.Location ?? "Virtual",
      },
      {
        title: "Status",
        key: "status",
        render: (_, record) => {
          const status = record.status ?? record.Status;
          return <Tag color={status === "Cancelled" ? "red" : "green"}>{status || "—"}</Tag>;
        },
      },
      {
        title: "Notes",
        dataIndex: "notes",
        key: "notes",
        render: (value, record) => value ?? record.Notes ?? "—",
      },
      {
        title: "Action",
        key: "action",
        render: (_, record) => {
          const manageUrl = record.manageUrl ?? record.ManageUrl;
          const status = record.status ?? record.Status;
          // ManageUrl is the backend-confirmed cancellation mechanism - "Use
          // this to cancel a Booked appointment on the patient's behalf."
          // There is no separate admin cancel-with-reason endpoint, and admin
          // must be able to cancel regardless of the 48h cutoff that applies
          // to a patient's own in-app cancel button - going straight to
          // Zoom's manage link bypasses that check entirely (it's not an
          // ownership- or time-gated in-app action).
          if (status === "Cancelled") return null;
          if (!manageUrl) {
            return (
              <Button danger disabled>
                No manage link
              </Button>
            );
          }
          return (
            <Button
              danger
              icon={<ExportOutlined />}
              onClick={() => window.open(manageUrl, "_blank", "noopener,noreferrer")}
            >
              Cancel on Zoom
            </Button>
          );
        },
      },
    ],
    [],
  );

  return (
    <Row gutter={16} style={{ padding: "0 5%" }}>
      <Col xs={24} md={24} style={{ paddingBottom: 16 }}>
        <p style={{ color: "#335CAD", fontWeight: "bold", fontSize: 16 }}>
          Manage Appointments
        </p>

        <Card style={{ marginBottom: 16 }}>
          <Row gutter={12}>
            <Col xs={24} md={14}>
              <RangePicker
                style={{ width: "100%" }}
                value={dateRange}
                onChange={(range) => {
                  setPage(1);
                  setDateRange(range);
                }}
              />
            </Col>
            <Col xs={24} md={6}>
              <Button icon={<ReloadOutlined />} onClick={fetchAppointments} style={{ width: "100%" }}>
                Refresh
              </Button>
            </Col>
          </Row>
        </Card>

        <Card>
          <Table
            columns={columns}
            dataSource={appointments}
            loading={loading}
            rowKey={(record) => record.appointmentId ?? record.AppointmentId}
            scroll={{ x: "max-content" }}
            pagination={{
              current: page,
              pageSize,
              total: totalRecords,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                setPageSize(nextSize);
              },
            }}
          />
        </Card>
      </Col>
    </Row>
  );
}
